import { Fragment, useEffect, useState } from 'react'
import {
  api, ApiError, can,
  type BroadcastDetail, type MessageCounts, type MessageLine, type MessagesDay, type MessagesMonth,
} from '../lib/api'
import './messages-report.css'

/** THE MESSAGES REPORT (5 Oct 2026), the second option under Reports.
 *
 *  The operator: "how many text messages was sent this month, how many
 *  emails was sent this month. And when we click on it, we should see every
 *  day how the messages went through or not ... I should have some logs of
 *  that. And if there is any errors coming back, we should see it."
 *
 *  THE WORDS ARE TRUE. "Accepted" is what the text server or the email
 *  provider took; the text server never says "delivered", so this never
 *  does. "Carrier error" is the code the carrier answered with. "Failed"
 *  never left, and says why. "Bounced" is the email provider's word. "On
 *  its way" is still queued, and is not counted as accepted.
 *
 *  A month, then a day, then one broadcast exactly: every member it went to
 *  and what became of each. Names and unit numbers, never a phone number;
 *  a callback body IS the caller's number and the server withholds it. */

const KIND_WORDS: Record<string, string> = {
  initial: 'First broadcast',
  rebroadcast: 'Re-broadcast',
  relocation: 'New address',
  collection: 'Collection',
  details: 'Details to the member going',
  site: 'Gate codes and notes',
  callback: 'Callback number',
  removed: 'Taken off the call',
  note: 'Note to the member going',
  call_closed: 'Call closed, to the members who went',
  reply: 'Reply from Messaging',
  announcement: 'Group message',
  combined: 'Combined rebroadcast',
  combined_first: 'New calls together',
}
const OUTCOME_WORDS: Record<string, string> = {
  accepted: 'Accepted',
  delivered: 'Delivered',
  carrier_error: 'Carrier error',
  bounced: 'Bounced',
  failed: 'Failed',
  queued: 'On its way',
}
const TONE: Record<string, string> = {
  accepted: 'mr-ok', delivered: 'mr-ok', carrier_error: 'mr-bad', bounced: 'mr-bad',
  failed: 'mr-warn', queued: 'mr-dim',
}
const WITHHELD: Record<string, string> = {
  entry_codes: 'What was sent is a gate code. Your role does not hold the entry-codes tick, so it is not shown here.',
  caller_number: "What was sent is the caller's phone number. A number is never shown in a report; it is on the call.",
}
type Tile = 'texts' | 'carrier' | 'failed' | 'email' | 'bounced'
type Chip = 'all' | 'problems' | 'text' | 'email'

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
function monthName(key: string): string {
  const [y, m] = key.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString([], { month: 'long', year: 'numeric' })
}
function shift(key: string, by: number): string {
  const [y, m] = key.split('-').map(Number)
  return monthKey(new Date(y, m - 1 + by, 1))
}
function dayName(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })
}
/** Times in the ORGANIZATION'S zone, the one the day rows are cut in: a
 *  text at 23:30 in Montreal must not read as the next day's 03:30 to a
 *  dispatcher whose laptop is set to UTC. */
function clock(iso: string, tz?: string): string {
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false }
  try { return new Date(iso).toLocaleTimeString([], { ...opts, timeZone: tz }) }
  catch { return new Date(iso).toLocaleTimeString([], opts) }
}
function stamp(iso: string, tz?: string): string {
  try { return new Date(iso).toLocaleString([], { hour12: false, timeZone: tz }) }
  catch { return new Date(iso).toLocaleString([], { hour12: false }) }
}
function n(v: number | undefined | null, bad?: string): JSX.Element {
  if (!v) return <span className="mr-dim">–</span>
  return <span className={bad ?? ''}>{v.toLocaleString()}</span>
}
function reason(outcome: string, why: string | null | undefined): string {
  if (outcome === 'carrier_error') return `Carrier error ${why ?? ''}`.trim()
  if (why) return `${OUTCOME_WORDS[outcome] ?? outcome}: ${why}`
  return OUTCOME_WORDS[outcome] ?? outcome
}
function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}
function summary(c: MessageCounts | null | undefined, kind: 'sms' | 'email'): string {
  if (!c) return '–'
  const parts: string[] = []
  const good = c.accepted + c.delivered
  if (good) parts.push(`${good} ${c.delivered ? 'delivered or accepted' : 'accepted'}`)
  if (kind === 'sms' && c.carrier_errors) parts.push(plural(c.carrier_errors, 'carrier error'))
  if (kind === 'email' && c.bounced) parts.push(`${c.bounced} bounced`)
  if (c.failed) parts.push(`${c.failed} failed`)
  if (c.queued) parts.push(`${c.queued} on its way`)
  return parts.join(' · ') || '–'
}
/** Something went wrong with it: a carrier error, a bounce or a failure. */
function problem(l: MessageLine): boolean {
  if (l.line === 'reply') return l.outcome !== 'accepted' && l.outcome !== 'delivered' && l.outcome !== 'queued'
  return !!((l.sms && (l.sms.carrier_errors || l.sms.failed)) || (l.email && (l.email.bounced || l.email.failed)))
}
/** It never left -- the "Failed to send" tile's own meaning, narrower than
 *  `problem`, so the tile's number and the lines under it agree. */
function failedOnly(l: MessageLine): boolean {
  if (l.line === 'reply') return l.outcome === 'failed'
  return !!((l.sms && l.sms.failed) || (l.email && l.email.failed))
}

function Detail({ id, tz }: { id: string; tz?: string }) {
  const [d, setD] = useState<BroadcastDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    api.messagesBroadcast(id)
      .then((x) => { if (alive) setD(x) })
      .catch((e) => { if (alive) setErr(e instanceof ApiError ? e.message : 'Could not open it.') })
    return () => { alive = false }
  }, [id])
  if (err) return <div className="mr-note">{err}</div>
  if (!d) return <div className="mr-empty">Loading…</div>
  return (
    <>
      <div className="mr-detail">
        <div className="mr-facts">
          <b>{d.incident_number && <span className="mr-code">{d.incident_number}</span>} {d.nature_label ?? ''}</b><br />
          {KIND_WORDS[d.kind] ?? d.kind} · {stamp(d.at, tz)}<br />
          Sent by {d.sent_by_label ?? 'the system'} to {plural(d.recipient_count, 'member')}
          {d.audience ? ` · aimed at ${d.audience.join(', ')}` : ' · everyone on duty'}
          {d.unreached && d.unreached.length > 0 && ` · ${d.unreached.length} could not be reached`}
        </div>
        <div>
          {d.body_withheld
            ? <div className="mr-withheld">{WITHHELD[d.withheld_reason ?? ''] ?? 'What was sent is not shown here.'}</div>
            : <div className="mr-body">{d.body}</div>}
        </div>
      </div>
      <table className="mr-table">
        <thead><tr><th>Member</th><th>How</th><th>What came back</th><th>Reference</th><th>Queued</th><th>Last word</th></tr></thead>
        <tbody>
          {d.deliveries.map((x) => (
            <tr key={x.id}>
              <td>{x.callsign && <span className="mr-mono">{x.callsign} </span>}{x.member_label ?? <span className="mr-dim">a member</span>}</td>
              <td><span className={`mr-pill ${x.channel === 'sms' ? '' : 'mr-dim'}`}>{x.channel === 'sms' ? 'text' : x.channel}</span>
                {x.part && x.parts ? <span className="mr-dim"> part {x.part} of {x.parts}</span> : null}</td>
              <td className={TONE[x.outcome] ?? ''}>{reason(x.outcome, x.reason)}</td>
              <td className="mr-mono mr-dim">{x.provider_ref ? x.provider_ref.slice(0, 12) + (x.provider_ref.length > 12 ? '…' : '') : '–'}</td>
              <td className="mr-mono">{clock(x.queued_at, tz)}</td>
              <td className="mr-mono mr-dim">{x.status_at ? clock(x.status_at, tz) : '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

function DayLines({ day, chip, tile }: { day: MessagesDay; chip: Chip; tile: Tile | null }) {
  const [open, setOpen] = useState<string | null>(null)
  const tz = day.timezone
  const lines = day.lines.filter((l) => {
    if (chip === 'problems' && !problem(l)) return false
    if (chip === 'text' && !(l.line === 'reply' || l.sms)) return false
    if (chip === 'email' && !(l.line === 'broadcast' && l.email)) return false
    if (tile === 'texts' && !(l.line === 'reply' || l.sms)) return false
    if (tile === 'carrier' && !(l.line === 'reply' ? l.outcome === 'carrier_error' : l.sms && l.sms.carrier_errors)) return false
    if (tile === 'failed' && !failedOnly(l)) return false
    if (tile === 'email' && !(l.line === 'broadcast' && l.email)) return false
    if (tile === 'bounced' && !(l.email && l.email.bounced)) return false
    return true
  })
  if (lines.length === 0) return <div className="mr-empty">Nothing to show for this choice.</div>
  return (
    <table className="mr-table">
      <thead><tr><th></th><th>Time</th><th>Call</th><th>What was sent</th><th>To</th><th>Texts</th><th>Emails</th><th>By</th></tr></thead>
      <tbody>
        {lines.map((l) => (
          <Fragment key={l.id}>
            <tr className={`mr-row ${open === l.id ? 'mr-open' : ''}`}
                onClick={() => setOpen(open === l.id ? null : (l.line === 'broadcast' ? l.id : null))}>
              <td><span className="mr-arrow">{l.line === 'broadcast' ? (open === l.id ? '▾' : '▸') : ''}</span></td>
              <td className="mr-mono">{clock(l.at, tz)}</td>
              <td>{l.incident_number ? <span className="mr-code">{l.incident_number}</span> : <span className="mr-dim">–</span>}</td>
              <td>{KIND_WORDS[l.kind] ?? l.kind}</td>
              <td>{l.line === 'reply' ? l.to_label : plural(l.recipient_count ?? 0, 'member')}</td>
              <td className={l.line === 'reply' ? (TONE[l.outcome ?? ''] ?? '') : (l.sms && (l.sms.carrier_errors || l.sms.failed) ? 'mr-bad' : '')}>
                {l.line === 'reply' ? reason(l.outcome ?? 'queued', l.reason) : summary(l.sms, 'sms')}
              </td>
              <td className={l.email && (l.email.bounced || l.email.failed) ? 'mr-bad' : ''}>
                {l.line === 'reply' ? <span className="mr-dim">–</span> : summary(l.email, 'email')}
              </td>
              <td className="mr-dim">{l.sent_by_label ?? ''}</td>
            </tr>
            {open === l.id && l.line === 'broadcast' && (
              <tr><td colSpan={8} className="mr-inner"><Detail id={l.id} tz={tz} /></td></tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}

export function MessagesReport() {
  const [month, setMonth] = useState(() => monthKey(new Date()))
  const [data, setData] = useState<MessagesMonth | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openDay, setOpenDay] = useState<string | null>(null)
  const [dayData, setDayData] = useState<MessagesDay | null>(null)
  const [tile, setTile] = useState<Tile | null>(null)
  const [chip, setChip] = useState<Chip>('all')
  const [busy, setBusy] = useState(false)

  // THE MONTH IS LOADED INSIDE THE EFFECT, so two quick presses of the
  // arrow cannot land the slower, older month under the newer header: the
  // reply for a month no longer chosen is dropped.
  useEffect(() => {
    let alive = true
    setOpenDay(null)
    setDayData(null)
    setData(null)
    api.messagesMonth(month)
      .then((d) => { if (alive) { setData(d); setError(null) } })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : 'Could not load the report.') })
    return () => { alive = false }
  }, [month])
  useEffect(() => {
    if (!openDay) { setDayData(null); return }
    let alive = true
    api.messagesDay(openDay)
      .then((d) => { if (alive) { setDayData(d); setError(null) } })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : 'Could not load the day.') })
    return () => { alive = false }
  }, [openDay])

  async function download() {
    setBusy(true)
    try {
      const blob = await api.messagesCsv(month)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `messages-${month}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not export.')
    } finally {
      setBusy(false)
    }
  }

  const sms = data?.sms
  const email = data?.email
  const failed = (sms?.failed ?? 0) + (email?.failed ?? 0)
  const tiles: { key: Tile; big: number; label: string; sub: string; tone: string }[] = [
    { key: 'texts', big: (sms?.accepted ?? 0) + (sms?.delivered ?? 0), label: 'Texts accepted', sub: 'handed to your text server', tone: '' },
    { key: 'carrier', big: sms?.carrier_errors ?? 0, label: 'Carrier errors', sub: 'refused by the carrier', tone: sms?.carrier_errors ? 'mr-bad' : 'mr-ok' },
    { key: 'failed', big: failed, label: 'Failed to send', sub: 'never left', tone: failed ? 'mr-warn' : 'mr-ok' },
    { key: 'email', big: (email?.accepted ?? 0) + (email?.delivered ?? 0), label: 'Emails accepted', sub: 'handed to the email provider', tone: '' },
    { key: 'bounced', big: email?.bounced ?? 0, label: 'Emails bounced', sub: 'reported by the provider', tone: email?.bounced ? 'mr-bad' : 'mr-ok' },
  ]
  const onWay = (sms?.queued ?? 0) + (email?.queued ?? 0)

  return (
    <>
      <div className="mr-head">
        <h2>Messages</h2>
        <div className="mr-month">
          <button type="button" onClick={() => setMonth(shift(month, -1))} aria-label="Earlier month">◀</button>
          <span>{monthName(month)}</span>
          <button type="button" onClick={() => setMonth(shift(month, 1))} aria-label="Later month"
                  disabled={month >= monthKey(new Date())}>▶</button>
        </div>
        {can('reporting.export') && (
          <button type="button" className="mr-btn mr-sm" disabled={busy || !data} onClick={download}>
            {busy ? 'Preparing…' : 'Download this month (CSV)'}
          </button>
        )}
      </div>
      {error && <div className="mr-note">{error}</div>}

      <div className="mr-tiles">
        {tiles.map((t) => (
          <button type="button" key={t.key} className={`mr-tile ${t.tone} ${tile === t.key ? 'mr-on' : ''}`}
                  aria-pressed={tile === t.key} title={tile === t.key ? 'Show everything again' : 'Show only these in the day log'}
                  onClick={() => setTile(tile === t.key ? null : t.key)}>
            <div className="mr-big">{data ? t.big.toLocaleString() : '–'}</div>
            <div className="mr-lbl">{t.label}</div>
            <div className="mr-sub">{t.sub}</div>
          </button>
        ))}
      </div>
      {onWay > 0 && <div className="mr-note mr-quiet">{plural(onWay, 'message')} still on the way out, not counted as accepted.</div>}

      <div className="mr-card">
        <table className="mr-table">
          <thead><tr>
            <th></th><th>Day</th><th className="mr-num">Calls</th>
            <th className="mr-num">Texts accepted</th><th className="mr-num">Carrier errors</th><th className="mr-num">Texts failed</th>
            <th className="mr-num">Emails accepted</th><th className="mr-num">Bounced</th><th className="mr-num">Emails failed</th>
          </tr></thead>
          <tbody>
            {!data && !error && (
              <tr><td colSpan={9} className="mr-empty">Loading…</td></tr>
            )}
            {data && data.days.length === 0 && (
              <tr><td colSpan={9} className="mr-empty">Nothing was sent in {monthName(month)}.</td></tr>
            )}
            {data?.days.map((d) => (
              <Fragment key={d.day}>
                <tr className={`mr-row ${openDay === d.day ? 'mr-open' : ''}`}
                    onClick={() => setOpenDay(openDay === d.day ? null : d.day)}>
                  <td><span className="mr-arrow">{openDay === d.day ? '▾' : '▸'}</span></td>
                  <td><b>{dayName(d.day)}</b>{d.replies ? <span className="mr-dim"> · {d.replies} repl{d.replies === 1 ? 'y' : 'ies'}</span> : null}</td>
                  <td className="mr-num">{n(d.calls)}</td>
                  <td className="mr-num">{n((d.sms?.accepted ?? 0) + (d.sms?.delivered ?? 0))}</td>
                  <td className="mr-num">{n(d.sms?.carrier_errors, 'mr-bad')}</td>
                  <td className="mr-num">{n(d.sms?.failed, 'mr-warn')}</td>
                  <td className="mr-num">{n((d.email?.accepted ?? 0) + (d.email?.delivered ?? 0))}</td>
                  <td className="mr-num">{n(d.email?.bounced, 'mr-bad')}</td>
                  <td className="mr-num">{n(d.email?.failed, 'mr-warn')}</td>
                </tr>
                {openDay === d.day && (
                  <tr><td colSpan={9} className="mr-inner">
                    <div className="mr-tools">
                      <span className="mr-dim" style={{ fontSize: 12.5, marginRight: 6 }}>The log for <b>{dayName(d.day)}</b>, newest first.</span>
                      {(['all', 'problems', 'text', 'email'] as Chip[]).map((c) => (
                        <button type="button" key={c} className={`mr-chip ${chip === c ? 'mr-on' : ''}`}
                                aria-pressed={chip === c} onClick={() => setChip(c)}>
                          {c === 'all' ? 'Everything' : c === 'problems' ? 'Problems only' : c === 'text' ? 'Texts' : 'Emails'}
                        </button>
                      ))}
                      {tile && <span className="mr-dim" style={{ fontSize: 12 }}>· narrowed to the pressed tile</span>}
                    </div>
                    {dayData && dayData.day === d.day
                      ? <DayLines day={dayData} chip={chip} tile={tile} />
                      : <div className="mr-empty">Loading…</div>}
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        <div className="mr-legend">
          <div><b>Accepted</b> — your text server, or the email provider, took it and gave a reference. The text server does not report delivery, so this report does not claim it.</div>
          <div><b>Carrier error</b> — the text server passed it on and the carrier answered with an error code. The CRM asks the text server for these every ten minutes.</div>
          <div><b>Failed</b> — it never left: the key was rate-limited, the provider could not be reached, a number could not be read. The reason is on the line.</div>
          <div><b>Bounced</b> — the email provider reported it afterwards. Needs the email key to be allowed to read activity at the provider.</div>
        </div>
      </div>
    </>
  )
}
